import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useDashboardOverviewQuery } from '../../services/dashboard';

const COLORS = ['#f59e0b', '#22c55e', '#ef4444'];

const formatCurrency = (value) => new Intl.NumberFormat('th-TH', {
  style: 'currency',
  currency: 'THB',
  minimumFractionDigits: 2,
}).format(Number(value || 0));

const formatNumber = (value) => new Intl.NumberFormat('th-TH').format(Number(value || 0));

function MetricCard({
  title, value, subtitle, icon, color, onClick,
}) {
  return (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%', alignItems: 'stretch' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography color="text.secondary" variant="body2">{title}</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>{value}</Typography>
              <Typography color="text.secondary" variant="caption">{subtitle}</Typography>
            </Box>
            <Box sx={{
              width: 46, height: 46, borderRadius: 2.5, display: 'grid', placeItems: 'center', color, bgcolor: `${color}18`,
            }}
            >
              {icon}
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{title}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { value: merchant, allMerchant } = useSelector((state) => state.merchant);
  const sorgcode = allMerchant || merchant || '';
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month'));
  const invalidPeriod = startDate.isAfter(endDate, 'day');

  const query = {
    sorgcode,
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
  };
  const {
    data: response,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useDashboardOverviewQuery(query, { skip: !sorgcode || invalidPeriod });
  const overview = response?.data;

  const paymentPath = `/payment-history?paymentStatus=Success&startDate=${query.startDate}&endDate=${query.endDate}`;

  const evidenceChart = useMemo(() => [
    {
      key: 'pending', name: t('app.pendingStatus'), value: overview?.evidence?.period?.pending || 0, path: '/pending',
    },
    {
      key: 'approved', name: t('app.approvedStatus'), value: overview?.evidence?.period?.approved || 0, path: '/approved',
    },
    {
      key: 'rejected', name: t('app.rejectedStatus'), value: overview?.evidence?.period?.rejected || 0, path: '/rejected',
    },
  ], [overview, t]);
  const metricCards = [
    {
      title: t('app.dashboardPaidAmount'),
      value: formatCurrency(overview?.payments?.amount),
      subtitle: t('app.dashboardSelectedPeriod'),
      icon: <AccountBalanceWalletOutlinedIcon />,
      color: '#16a34a',
      onClick: () => navigate(paymentPath),
    },
    {
      title: t('app.dashboardPaidTransactions'),
      value: formatNumber(overview?.payments?.count),
      subtitle: t('app.dashboardSelectedPeriod'),
      icon: <CheckCircleOutlineIcon />,
      color: '#0ea5e9',
      onClick: () => navigate(paymentPath),
    },
    {
      title: t('app.dashboardPendingEvidence'),
      value: formatNumber(overview?.evidence?.backlog?.pending),
      subtitle: t('app.dashboardAllBacklog'),
      icon: <HourglassTopIcon />,
      color: '#f59e0b',
      onClick: () => navigate('/pending'),
    },
    {
      title: t('app.dashboardRejectedEvidence'),
      value: formatNumber(overview?.evidence?.backlog?.rejected),
      subtitle: t('app.dashboardAllBacklog'),
      icon: <HighlightOffIcon />,
      color: '#dc2626',
      onClick: () => navigate('/rejected'),
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>{t('app.dashboardTitle')}</Typography>
          <Typography color="text.secondary">{t('app.dashboardSubtitle')}</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker label={t('app.startDate')} value={startDate} onChange={(value) => value && setStartDate(value)} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label={t('app.endDate')} value={endDate} onChange={(value) => value && setEndDate(value)} slotProps={{ textField: { size: 'small' } }} />
          </LocalizationProvider>
          <Button variant="contained" startIcon={isFetching ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />} onClick={refetch} disabled={!sorgcode || invalidPeriod || isFetching}>
            {t('app.dashboardRefresh')}
          </Button>
        </Stack>
      </Stack>

      {!sorgcode && <Alert severity="info" sx={{ mb: 3 }}>{t('app.dashboardSelectCompany')}</Alert>}
      {invalidPeriod && <Alert severity="warning" sx={{ mb: 3 }}>{t('app.dashboardInvalidPeriod')}</Alert>}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={refetch}>{t('app.dashboardRetry')}</Button>}>
          {error?.data?.error?.message || t('app.dashboardLoadError')}
        </Alert>
      )}

      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Skeleton variant="rounded" height={130} />
            </Grid>
          ))}
        </Grid>
      ) : overview && (
        <>
          <Grid container spacing={2}>
            {metricCards.map((card) => (
              <Grid item xs={12} sm={6} lg={3} key={card.title}>
                <MetricCard {...card} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} lg={8}>
              <ChartCard title={t('app.dashboardPaymentTrend')}>
                <Box sx={{ height: 330, cursor: 'pointer' }} onClick={() => navigate(paymentPath)}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={overview.payments.daily}
                      margin={{
                        top: 12, right: 12, left: 8, bottom: 4,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" tickFormatter={(value) => dayjs(value).format('DD/MM')} minTickGap={24} />
                      <YAxis yAxisId="amount" tickFormatter={(value) => new Intl.NumberFormat('th-TH', { notation: 'compact' }).format(value)} />
                      <YAxis yAxisId="count" orientation="right" allowDecimals={false} />
                      <Tooltip formatter={(value, name) => (name === t('app.dashboardAmount') ? formatCurrency(value) : formatNumber(value))} labelFormatter={(value) => dayjs(value).format('DD/MM/YYYY')} />
                      <Legend />
                      <Bar yAxisId="amount" dataKey="amount" name={t('app.dashboardAmount')} fill="#86efac" radius={[5, 5, 0, 0]} />
                      <Line yAxisId="count" type="monotone" dataKey="count" name={t('app.dashboardTransactions')} stroke="#2563eb" strokeWidth={3} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </ChartCard>
            </Grid>
            <Grid item xs={12} lg={4}>
              <ChartCard title={t('app.dashboardEvidenceStatus')}>
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={evidenceChart} dataKey="value" nameKey="name" innerRadius={52} outerRadius={84} paddingAngle={3} onClick={(entry) => entry?.path && navigate(entry.path)}>
                        {evidenceChart.map((entry, index) => <Cell key={entry.key} fill={COLORS[index]} style={{ cursor: 'pointer' }} />)}
                      </Pie>
                      <Tooltip formatter={(value) => formatNumber(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </ChartCard>
            </Grid>
          </Grid>

          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mt: 2 }}>
            <Chip label={`${t('app.dashboardCompany')}: ${sorgcode}`} variant="outlined" />
            <Typography variant="caption" color="text.secondary">
              {t('app.dashboardLastUpdated')}
              :
              {dayjs(overview.generatedAt).format('DD/MM/YYYY HH:mm:ss')}
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  );
}

export default Dashboard;
